import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";

/**
 * Change-password dialog. Automatically routes:
 *  - v2 super-admin / rbac users → /v2/auth/change-password (+ /v2/auth/change-email)
 *  - legacy env-backed admin      → /auth/change-password (supports optional new_email)
 */
export default function ChangePasswordButton() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [cur, setCur]   = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const isV2User = Boolean(user?.id) || Array.isArray(user?.permissions) || user?.role === "super_admin";
  const reset = () => { setCur(""); setNext(""); setConfirm(""); setNewEmail(""); setShowPw(false); };

  const submit = async () => {
    if (!cur) { toast.error("Enter current password"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Confirmation does not match"); return; }
    const wantsEmailChange = Boolean(newEmail && newEmail.trim() && newEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase());
    setSaving(true);
    try {
      if (isV2User) {
        await api.post("/v2/auth/change-password", { current_password: cur, new_password: next });
        if (wantsEmailChange) {
          try {
            await api.post("/v2/auth/change-email", { password: next, new_email: newEmail.trim() });
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Password changed but email update failed");
            setOpen(false); reset();
            setTimeout(() => { logout(); }, 800);
            return;
          }
        }
      } else {
        const body = { current_password: cur, new_password: next };
        if (wantsEmailChange) body.new_email = newEmail.trim();
        const r = await api.post("/auth/change-password", body);
        if (r.data?.token) {
          try { localStorage.setItem("adelina_token", r.data.token); } catch (err) {
            // localStorage can throw in private-browsing / quota-exceeded modes.
            console.warn("localStorage.setItem failed for admin token:", err);
          }
        }
      }
      toast.success("Password updated. Please sign in again with the new credentials.");
      setOpen(false); reset();
      setTimeout(() => { logout(); }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const strengthLabel = (() => {
    if (!next) return { text: "", cls: "text-zinc-400" };
    const score = (next.length >= 12 ? 2 : next.length >= 8 ? 1 : 0)
      + (/[A-Z]/.test(next) ? 1 : 0)
      + (/[0-9]/.test(next) ? 1 : 0)
      + (/[^A-Za-z0-9]/.test(next) ? 1 : 0);
    if (score >= 4) return { text: "Strong",  cls: "text-emerald-600" };
    if (score >= 3) return { text: "Good",    cls: "text-amber-600" };
    if (score >= 2) return { text: "Weak",    cls: "text-orange-600" };
    return { text: "Very weak", cls: "text-red-600" };
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-300 hover:bg-zinc-950 hover:text-white transition-colors"
          title="Change password"
          data-testid="admin-change-password-open"
        >
          <KeyRound size={14} /> Password
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Admin Password</DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="change-password-form">
          <div>
            <Label>Current password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" data-testid="cp-current" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-800" title={showPw ? "Hide" : "Show"} data-testid="cp-toggle-visibility">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <Label>New password</Label>
            <Input type={showPw ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="cp-new" />
            {next && (<div className={"text-xs mt-1 " + strengthLabel.cls} data-testid="cp-strength">Strength: {strengthLabel.text}</div>)}
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="cp-confirm" />
            {confirm && confirm !== next && (<div className="text-xs text-red-600 mt-1">Does not match new password</div>)}
          </div>
          <div>
            <Label>Change email (optional)</Label>
            <Input type="email" placeholder={user?.email || "admin@example.com"} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} autoComplete="email" data-testid="cp-email" />
            <div className="text-[11px] text-zinc-400 mt-1">Leave blank to keep <code>{user?.email}</code></div>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            After saving, you will be signed out and must log in again with the new credentials. Store them somewhere safe — there is no email-based recovery for the legacy admin account.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setOpen(false); reset(); }} className="px-4 py-2 rounded-full text-sm border border-zinc-300 hover:bg-zinc-50" data-testid="cp-cancel">Cancel</button>
            <button onClick={submit} disabled={saving || !cur || !next || next !== confirm} className="btn-gold text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="cp-save">
              {saving ? (<span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Saving…</span>) : "Save & Sign out"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
